import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

export default function ConnectAccounts({ token, userId, platforms = {}, onReady, apiBaseUrl = '' }) {
  const [sessionStatus, setSessionStatus] = useState({
    linkedin: 'unknown',
    dice: 'unknown',
    indeed: 'unknown',
    monster: 'unknown',
  });
  const [activePlatform, setActivePlatform] = useState(null);
  const [screenshot, setScreenshot] = useState(null);
  const [inputText, setInputText] = useState('');
  const pollRef = useRef({});
  const textInputRef = useRef(null);
  const apiHeaders = { Authorization: `Bearer ${token}` };

  const selectedPlatforms = Object.keys(platforms).filter(p => platforms[p]);

  useEffect(() => {
    selectedPlatforms.forEach(checkSession);
  }, []); // eslint-disable-line

  useEffect(() => {
    if (selectedPlatforms.length === 0) return;
    const allConnected = selectedPlatforms.every(p => sessionStatus[p] === 'connected');
    if (allConnected && onReady) onReady();
  }, [sessionStatus]); // eslint-disable-line

  async function checkSession(platform) {
    setSessionStatus(s => ({ ...s, [platform]: 'checking' }));
    try {
      const { data } = await axios.post(`${apiBaseUrl}/api/auto-apply/check-session`, { platform }, { headers: apiHeaders });
      setSessionStatus(s => ({ ...s, [platform]: data.loggedIn ? 'connected' : 'idle' }));
    } catch {
      setSessionStatus(s => ({ ...s, [platform]: 'idle' }));
    }
  }

  async function startLogin(platform) {
    setSessionStatus(s => ({ ...s, [platform]: 'connecting' }));
    try {
      const { data } = await axios.post(`${apiBaseUrl}/api/auto-apply/start-login`, { platform }, { headers: apiHeaders });

      if (data.status === 'browser_opened') {
        setSessionStatus(s => ({ ...s, [platform]: 'waiting' }));
        pollForLogin(platform);
      } else {
        setScreenshot(data.screenshot);
        setActivePlatform(platform);
        setSessionStatus(s => ({ ...s, [platform]: 'connecting' }));
        startPolling(platform);
      }
    } catch (err) {
      setSessionStatus(s => ({ ...s, [platform]: 'idle' }));
      alert('Failed to open browser: ' + (err.response?.data?.error || err.message));
    }
  }

  function startPolling(platform) {
    if (pollRef.current[platform]) clearInterval(pollRef.current[platform]);
    pollRef.current[platform] = setInterval(async () => {
      try {
        const { data } = await axios.get(
          `${apiBaseUrl}/api/auto-apply/login-screenshot/${userId}?platform=${platform}`,
          { headers: apiHeaders }
        );
        setScreenshot(data.screenshot);
        if (data.status === 'logged_in') {
          clearInterval(pollRef.current[platform]);
          setSessionStatus(s => ({ ...s, [platform]: 'connected' }));
          setActivePlatform(null);
          setScreenshot(null);
        }
      } catch {
        clearInterval(pollRef.current[platform]);
        checkSession(platform);
      }
    }, 1200);
  }

  function pollForLogin(platform) {
    if (pollRef.current[platform]) clearInterval(pollRef.current[platform]);
    pollRef.current[platform] = setInterval(async () => {
      try {
        const { data } = await axios.get(
          `${apiBaseUrl}/api/auto-apply/login-screenshot/${userId}?platform=${platform}`,
          { headers: apiHeaders }
        );
        if (data.status === 'logged_in') {
          clearInterval(pollRef.current[platform]);
          setSessionStatus(s => ({ ...s, [platform]: 'connected' }));
        }
      } catch {
        clearInterval(pollRef.current[platform]);
        checkSession(platform);
      }
    }, 2000);
  }

  async function handleImageClick(e) {
    if (!activePlatform) return;
    const rect = e.target.getBoundingClientRect();
    const x = Math.round((e.clientX - rect.left) * (1280 / rect.width));
    const y = Math.round((e.clientY - rect.top) * (800 / rect.height));
    try {
      await axios.post(`${apiBaseUrl}/api/auto-apply/login-interact/${userId}`, { type: 'click', x, y }, { headers: apiHeaders });
    } catch {}
    setTimeout(() => textInputRef.current?.focus(), 50);
  }

  async function handleKeyDown(e) {
    if (!activePlatform) return;
    if (e.key === 'Backspace') {
      await axios.post(`${apiBaseUrl}/api/auto-apply/login-interact/${userId}`, { type: 'key', key: 'Backspace' }, { headers: apiHeaders });
      setInputText(t => t.slice(0, -1));
      return;
    }
    if (e.key === 'Enter') {
      await axios.post(`${apiBaseUrl}/api/auto-apply/login-interact/${userId}`, { type: 'key', key: 'Enter' }, { headers: apiHeaders });
      setInputText('');
      return;
    }
    if (e.key === 'Tab') {
      e.preventDefault();
      await axios.post(`${apiBaseUrl}/api/auto-apply/login-interact/${userId}`, { type: 'key', key: 'Tab' }, { headers: apiHeaders });
      return;
    }
  }

  async function handleInputChange(e) {
    const newText = e.target.value;
    const added = newText.slice(inputText.length);
    setInputText(newText);
    if (added) {
      await axios.post(`${apiBaseUrl}/api/auto-apply/login-interact/${userId}`, { type: 'type', text: added }, { headers: apiHeaders });
    }
  }

  function cancelLogin() {
    if (activePlatform && pollRef.current[activePlatform]) {
      clearInterval(pollRef.current[activePlatform]);
    }
    setActivePlatform(null);
    setScreenshot(null);
    setInputText('');
    if (activePlatform) {
      setSessionStatus(s => ({ ...s, [activePlatform]: 'idle' }));
    }
  }

  const platformInfo = {
    linkedin: { label: 'LinkedIn', icon: '🔷' },
    dice:     { label: 'Dice',     icon: '🎲' },
    indeed:   { label: 'Indeed',   icon: '🔵' },
    monster:  { label: 'Monster',  icon: '👾' },
  };

  const statusIcon  = s => ({ connected: '✅', waiting: '⏳', connecting: '⏳', checking: '🔄', error: '❌' }[s] ?? '🔗');
  const statusLabel = s => ({ connected: 'Connected', waiting: 'Log in on the browser window that opened...', connecting: 'Opening browser...', checking: 'Checking...', error: 'Error' }[s] ?? 'Not connected');

  if (selectedPlatforms.length === 0) return null;

  return (
    <div style={{ marginBottom: 24 }}>
      <h3 style={{ marginBottom: 8, fontSize: 15, fontWeight: 600 }}>Connect Accounts</h3>
      <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 12 }}>
        Log in once — sessions are saved permanently for future runs.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {selectedPlatforms.map(platform => (
          <div key={platform} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '10px 14px', borderRadius: 8,
            border: '1px solid var(--color-border-tertiary)',
            background: 'var(--color-background-secondary)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 20 }}>{statusIcon(sessionStatus[platform])}</span>
              <div>
                <div style={{ fontWeight: 500, fontSize: 14 }}>
                  {platformInfo[platform]?.icon} {platformInfo[platform]?.label}
                </div>
                <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                  {statusLabel(sessionStatus[platform])}
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {(sessionStatus[platform] === 'idle' || sessionStatus[platform] === 'unknown') && (
                <button onClick={() => startLogin(platform)} style={{
                  padding: '6px 16px', borderRadius: 6, fontSize: 13,
                  background: '#4F46E5', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 500,
                }}>
                  Connect
                </button>
              )}
              {sessionStatus[platform] === 'connected' && (
                <button onClick={() => startLogin(platform)} style={{
                  padding: '6px 14px', borderRadius: 6, fontSize: 12,
                  background: 'transparent', color: 'var(--color-text-secondary)',
                  border: '1px solid var(--color-border-tertiary)', cursor: 'pointer',
                }}>
                  Reconnect
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {screenshot && activePlatform && (
        <div style={{
          border: '1px solid var(--color-border-secondary)', borderRadius: 10,
          overflow: 'hidden', marginTop: 16,
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '8px 12px', background: 'var(--color-background-tertiary)',
            borderBottom: '1px solid var(--color-border-tertiary)',
          }}>
            <span style={{ fontSize: 13, fontWeight: 500 }}>
              🌐 Log into {platformInfo[activePlatform]?.label} below
            </span>
            <button onClick={cancelLogin} style={{
              background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, lineHeight: 1,
            }}>×</button>
          </div>

          <img
            src={`data:image/jpeg;base64,${screenshot}`}
            alt="Browser"
            onClick={handleImageClick}
            style={{ width: '100%', display: 'block', cursor: 'pointer' }}
          />

          <div style={{
            padding: '8px 12px', borderTop: '1px solid var(--color-border-tertiary)',
            background: 'var(--color-background-secondary)',
            display: 'flex', gap: 8, alignItems: 'center',
          }}>
            <span style={{ fontSize: 12, color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>
              Type here:
            </span>
            <input
              type="text"
              value={inputText}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder="Click a field above then type here. Backspace & Enter supported."
              ref={textInputRef}
              autoFocus
              style={{
                flex: 1, padding: '6px 10px', borderRadius: 6, fontSize: 13,
                border: '1px solid var(--color-border-secondary)',
                background: 'var(--color-background-primary)',
                color: 'var(--color-text-primary)', outline: 'none',
              }}
            />
          </div>
          <div style={{ padding: '4px 12px 8px', fontSize: 11, color: 'var(--color-text-tertiary)' }}>
            Click on the email or password field in the image, then type in the box above.
          </div>
        </div>
      )}
    </div>
  );
}
